import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Clock, ArrowLeft, Award, TrendingUp, Crown, Medal, Star } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { fetchAllRankings } from '../lib/api';
import { formatTempo, getDiscordAvatarUrl } from '../lib/timeUtils';

const Ranking = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('global');
  const [rankings, setRankings] = useState({
    weekly: [],
    monthly: [],
    global: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    const loadRankings = async () => {
      // Evitar recarregamento se já carregou uma vez
      if (hasLoaded) return;
      
      if (!user?.discord_id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const rankingsData = await fetchAllRankings();
        
        // Mapear dados da API para o formato esperado
        const mapRankingData = (rankingArray, currentUserDiscordId, scoreField) => {
          if (!rankingArray || !Array.isArray(rankingArray)) return [];
          
          return rankingArray.map((item, index) => {
            const discordId = item.discord_id || item.user_id?.toString();
            const isCurrentUser = discordId === currentUserDiscordId;
            const score = item[scoreField] || 0;
            
            // Lógica de foto: se tem discord_avatar, usa; senão, foto padrão
            const avatar = item.discord_avatar 
              ? getDiscordAvatarUrl(discordId, item.discord_avatar)
              : getDiscordAvatarUrl(discordId, null);
            
            return {
              rank: index + 1,
              username: item.discord_username || item.nickname || 'Usuário',
              avatar: avatar,
              score: score,
              isCurrentUser: isCurrentUser,
              discord_id: discordId,
              total_seconds: item.total_seconds || 0,
              weekly_seconds: item.weekly_seconds || 0,
              monthly_seconds: item.monthly_seconds || 0
            };
          });
        };
        
        const mappedRankings = {
          weekly: mapRankingData(rankingsData?.weekly || [], user.discord_id, 'weekly_seconds'),
          monthly: mapRankingData(rankingsData?.monthly || [], user.discord_id, 'monthly_seconds'),
          global: mapRankingData(rankingsData?.global || [], user.discord_id, 'total_seconds')
        };
        
        setRankings(mappedRankings);
        setError(null);
        setHasLoaded(true);
      } catch (err) {
        console.error('Erro ao carregar rankings:', err);
        setError('Não foi possível carregar os rankings. Tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    };

    loadRankings();
  }, [user?.discord_id, hasLoaded]); // Usar apenas discord_id e flag de controle

  const currentRankings = rankings[activeTab] || [];
  const topUser = currentRankings[0];
  const otherUsers = currentRankings.slice(1);

  const getScoreField = () => {
    if (activeTab === 'global') return 'total_seconds';
    if (activeTab === 'weekly') return 'weekly_seconds';
    return 'monthly_seconds';
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="text-yellow-400" size={24} />;
    if (rank === 2) return <Medal className="text-gray-300" size={20} />;
    if (rank === 3) return <Medal className="text-orange-400" size={20} />;
    return <span className="text-gray-500 font-bold text-sm">#{rank}</span>;
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white';
    if (rank === 2) return 'bg-gradient-to-r from-gray-300 to-gray-400 text-black';
    if (rank === 3) return 'bg-gradient-to-r from-orange-400 to-orange-500 text-white';
    return 'bg-white/10 text-gray-400';
  };

  // Calcular porcentagem para gráfico (comparado com o primeiro lugar)
  const calculatePercentage = (score, maxScore) => {
    if (!maxScore || maxScore === 0) return 0;
    return Math.min((score / maxScore) * 100, 100);
  };

  const maxScore = topUser?.score || 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-purple-950 text-white">
      <div className="max-w-7xl mx-auto px-6 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <Link to="/feed" className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors">
            <ArrowLeft size={18} />
            <span>Voltar ao Feed</span>
          </Link>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full blur opacity-75"></div>
                <Trophy className="relative w-10 h-10 text-yellow-400" />
              </div>
              <div>
                <h1 className="text-4xl font-black">Ranking</h1>
                <p className="text-gray-400 text-sm">Competição de horas no servidor</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-3 mb-8 bg-black/40 backdrop-blur-md rounded-sm p-2 border border-white/10">
          <button
            onClick={() => setActiveTab('global')}
            className={`flex-1 py-3 px-4 font-bold text-sm uppercase tracking-wider transition-all rounded-sm ${
              activeTab === 'global'
                ? 'bg-gradient-to-r from-cyan-500 to-fuchsia-600 text-white shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            Global
          </button>
          <button
            onClick={() => setActiveTab('weekly')}
            className={`flex-1 py-3 px-4 font-bold text-sm uppercase tracking-wider transition-all rounded-sm ${
              activeTab === 'weekly'
                ? 'bg-gradient-to-r from-cyan-500 to-fuchsia-600 text-white shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            Semanal
          </button>
          <button
            onClick={() => setActiveTab('monthly')}
            className={`flex-1 py-3 px-4 font-bold text-sm uppercase tracking-wider transition-all rounded-sm ${
              activeTab === 'monthly'
                ? 'bg-gradient-to-r from-cyan-500 to-fuchsia-600 text-white shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            Mensal
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-400"></div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-sm p-6 text-center mb-8">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Content */}
        {!loading && !error && currentRankings.length > 0 && (
          <>
            {/* Top User Card - Destaque */}
            {topUser && (
              <div className="mb-8 relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 via-orange-500 to-yellow-400 rounded-lg blur-lg opacity-50"></div>
                <div className="relative bg-gradient-to-br from-yellow-500/20 via-orange-500/20 to-purple-900/40 backdrop-blur-xl border-2 border-yellow-400/50 rounded-lg p-8 overflow-hidden">
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-400 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-500 rounded-full blur-3xl"></div>
                  </div>
                  
                  <div className="relative z-10 flex flex-col md:flex-row items-center md:items-start gap-8">
                    {/* Avatar Section */}
                    <div className="relative">
                      <div className="absolute -inset-2 bg-yellow-400 rounded-full blur-md opacity-75"></div>
                      <div className="relative">
                        <img 
                          src={topUser.avatar} 
                          alt={topUser.username}
                          className="w-32 h-32 rounded-full border-4 border-yellow-400 object-cover"
                          onError={(e) => {
                            e.target.src = getDiscordAvatarUrl(topUser.discord_id || '0', null);
                          }}
                        />
                        <div className="absolute -bottom-2 -right-2 bg-yellow-400 rounded-full p-2">
                          <Crown className="text-black" size={24} />
                        </div>
                      </div>
                    </div>

                    {/* Info Section */}
                    <div className="flex-1 text-center md:text-left">
                      <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                        <span className="px-3 py-1 bg-yellow-400/20 text-yellow-400 rounded-full text-xs font-bold uppercase">
                          1º Lugar
                        </span>
                      </div>
                      <h2 className="text-4xl font-black mb-2 bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                        {topUser.username}
                      </h2>
                      
                      {/* Stats Grid */}
                      <div className="grid grid-cols-3 gap-4 mt-6">
                        <div className="bg-black/40 rounded-lg p-4 border border-white/10">
                          <div className="text-xs text-gray-400 mb-1">
                            {activeTab === 'global' ? 'Total' : activeTab === 'weekly' ? 'Semanal' : 'Mensal'}
                          </div>
                          <div className="text-2xl font-black text-yellow-400">
                            {formatTempo(topUser.score)}
                          </div>
                        </div>
                        <div className="bg-black/40 rounded-lg p-4 border border-white/10">
                          <div className="text-xs text-gray-400 mb-1">Total</div>
                          <div className="text-xl font-bold">{formatTempo(topUser.total_seconds)}</div>
                        </div>
                        <div className="bg-black/40 rounded-lg p-4 border border-white/10">
                          <div className="text-xs text-gray-400 mb-1">Semanal</div>
                          <div className="text-xl font-bold">{formatTempo(topUser.weekly_seconds)}</div>
                        </div>
                      </div>
                    </div>

                    {/* Trophy Icon */}
                    <div className="hidden md:block">
                      <div className="relative">
                        <div className="absolute inset-0 bg-yellow-400 rounded-full blur-2xl opacity-50"></div>
                        <Trophy className="relative w-24 h-24 text-yellow-400" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Other Users List */}
            {otherUsers.length > 0 && (
              <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-lg overflow-hidden">
                <div className="p-6 border-b border-white/10">
                  <h3 className="text-xl font-black flex items-center gap-2">
                    <Award className="text-cyan-400" size={24} />
                    Outros Competidores
                  </h3>
                </div>
                
                <div className="divide-y divide-white/10">
                  {otherUsers.map((player) => {
                    const percentage = calculatePercentage(player.score, maxScore);
                    const isCurrentUser = player.isCurrentUser;
                    
                    return (
                      <div
                        key={player.discord_id}
                        className={`p-4 hover:bg-white/5 transition-all ${
                          isCurrentUser ? 'bg-cyan-500/10 border-l-4 border-cyan-500' : ''
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          {/* Rank */}
                          <div className="w-12 flex justify-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getRankBadge(player.rank)}`}>
                              {player.rank}
                            </div>
                          </div>

                          {/* Avatar */}
                          <img 
                            src={player.avatar} 
                            alt={player.username}
                            className="w-12 h-12 rounded-full border-2 border-white/20 object-cover"
                            onError={(e) => {
                              e.target.src = getDiscordAvatarUrl(player.discord_id || '0', null);
                            }}
                          />

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className={`font-bold text-sm ${isCurrentUser ? 'text-cyan-400' : 'text-white'}`}>
                                {player.username}
                              </p>
                              {isCurrentUser && (
                                <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded text-xs font-bold">
                                  Você
                                </span>
                              )}
                            </div>
                            
                            {/* Progress Bar */}
                            <div className="flex items-center gap-3">
                              <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full transition-all ${
                                    player.rank === 2 
                                      ? 'bg-gradient-to-r from-gray-300 to-gray-400'
                                      : player.rank === 3
                                      ? 'bg-gradient-to-r from-orange-400 to-orange-500'
                                      : 'bg-gradient-to-r from-cyan-500 to-fuchsia-600'
                                  }`}
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-xs font-bold text-gray-400 min-w-[80px] text-right">
                                {formatTempo(player.score)}
                              </span>
                            </div>
                          </div>

                          {/* Rank Icon */}
                          <div className="w-8 flex justify-center">
                            {getRankIcon(player.rank)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty State */}
            {currentRankings.length === 0 && (
              <div className="bg-black/40 backdrop-blur-md border border-white/10 rounded-lg p-12 text-center">
                <Trophy className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">Nenhum ranking disponível ainda.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Ranking;
